import { type RefObject, useEffect, useRef, useState } from "react";
import { useReducedMotion, type MotionProps } from "framer-motion";

/** Entrance roles from site-transformation PRD §3. */
export type EntranceRole = "nav" | "eyebrow" | "h1" | "body" | "cta" | "belowFold";

type Spec = {
	delayMs: number;
	durationMs: number;
	y?: number;
	scaleFrom?: number;
};

const SPECS: Record<EntranceRole, Spec> = {
	nav: { delayMs: 0, durationMs: 200 },
	eyebrow: { delayMs: 80, durationMs: 300, y: 4 },
	h1: { delayMs: 140, durationMs: 350, y: 8 },
	body: { delayMs: 220, durationMs: 300, y: 4 },
	cta: { delayMs: 300, durationMs: 250, scaleFrom: 0.98 },
	belowFold: { delayMs: 0, durationMs: 300, y: 8 },
};

const EASE_OUT: [number, number, number, number] = [0.2, 0.8, 0.2, 1];

function buildMotion(
	role: EntranceRole,
	reduceMotion: boolean,
	show: boolean,
): MotionProps {
	const spec = SPECS[role];

	if (reduceMotion) {
		return {
			initial: { opacity: show ? 1 : 0 },
			animate: { opacity: show ? 1 : 0 },
			transition: { duration: 0.15, ease: "linear" },
			style: { pointerEvents: "auto" },
		};
	}

	return {
		initial: {
			opacity: 0,
			y: spec.y ?? 0,
			scale: spec.scaleFrom ?? 1,
		},
		animate: show
			? { opacity: 1, y: 0, scale: 1 }
			: { opacity: 0, y: spec.y ?? 0, scale: spec.scaleFrom ?? 1 },
		transition: {
			delay: role === "belowFold" && !show ? 0 : spec.delayMs / 1000,
			duration: spec.durationMs / 1000,
			ease: EASE_OUT,
		},
		style: { pointerEvents: "auto" },
	};
}

/**
 * Role-based page entrance motion (PRD §3).
 * Visual only — never gates pointer-events. Reduced motion → opacity-only or skip translate/scale.
 * For `belowFold`, also returns a `ref` for IntersectionObserver (threshold 0.2, once).
 */
export function useStaggeredEntrance(role: "belowFold"): MotionProps & {
	ref: RefObject<HTMLElement | null>;
};
export function useStaggeredEntrance(role: Exclude<EntranceRole, "belowFold">): MotionProps;
export function useStaggeredEntrance(
	role: EntranceRole,
): MotionProps | (MotionProps & { ref: RefObject<HTMLElement | null> }) {
	const reduceMotion = useReducedMotion() === true;
	const ref = useRef<HTMLElement | null>(null);
	const [visible, setVisible] = useState(role !== "belowFold");

	useEffect(() => {
		if (role !== "belowFold") return;
		const node = ref.current;
		if (!node) return;
		if (typeof IntersectionObserver === "undefined") {
			setVisible(true);
			return;
		}
		const io = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setVisible(true);
						io.disconnect();
						break;
					}
				}
			},
			{ threshold: 0.2 },
		);
		io.observe(node);
		return () => io.disconnect();
	}, [role]);

	const show = role === "belowFold" ? visible : true;
	const motion = buildMotion(role, reduceMotion, show);

	if (role === "belowFold") {
		return { ...motion, ref };
	}
	return motion;
}
